import qs from 'query-string';
import { useCallback } from 'react';
import { useAuthFetch } from '@cko/dashboard-shared';
import { QueryFunctionContext } from '@tanstack/react-query';

type ResponseLink = {
  self: { href: string };
};

type ProcessingChannelDto = {
  id: string;
  entity_id: string;
  name: string;
  date_created: string;
  business_model: string;
  _links: ResponseLink;
};

export type ProcessingChannelVo = {
  id: string;
  entityId: string;
  name: string;
};

const mapToVo = (channel: ProcessingChannelDto): ProcessingChannelVo => ({
  id: channel.id,
  entityId: channel.entity_id,
  name: channel.name,
});

type TGetChannels = {
  clientId: string;
  entityId?: string | null;
  processing_channel_feature: never;
};

type TGetChannelsWithMoto = {
  clientId: string;
  entityId?: string | string[] | null;
  processing_channel_feature?: string;
};

type Params = TGetChannels | TGetChannelsWithMoto;

export const useProcessingChannelsFetcher = (
  entitiesWithChannelsToDisplay: string[],
  minimumChannelsReturned: number = 0,
) => {
  const fetch = useAuthFetch();

  return useCallback(
    async ({ queryKey }: QueryFunctionContext) => {
      const [path, { clientId, entityId, processingChannelFeature }] = queryKey as [
        string,
        { clientId: string; entityId: string | null; processingChannelFeature?: string },
      ];

      const params: Params = {
        clientId,
      };

      if (entityId) {
        params.entityId = entityId;
      }

      if (processingChannelFeature) {
        params.processing_channel_feature = processingChannelFeature;
      }

      const response = await fetch(
        `${path}?${qs.stringify(params, {
          skipNull: true,
          sort: (a: string, b: string) => a.localeCompare(b),
        })}`,
      );

      const data = await response.json();
      const channels = data.channels
        .map(mapToVo)
        .filter((channel: ProcessingChannelVo) =>
          entitiesWithChannelsToDisplay.includes(channel.entityId),
        );

      const isUserAbleToAccessChannels = channels.length > minimumChannelsReturned;

      return isUserAbleToAccessChannels ? channels : [];
    },
    [fetch],
  );
};
